import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, ThemeEvents } from '@theme/events';

class MccmFrequentlyBoughtTogether extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    this.querySelector('[data-fbt-add]')?.addEventListener('click', this.#addSelected);
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleCurrentVariant);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.querySelector('[data-fbt-add]')?.removeEventListener('click', this.#addSelected);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleCurrentVariant);
  }

  #handleChange = (event) => {
    const variant = event.target.closest?.('[data-fbt-variant]');
    if (variant instanceof HTMLSelectElement) {
      const option = variant.selectedOptions[0];
      const item = variant.closest('[data-fbt-item]');
      if (item && option) {
        item.dataset.fbtPrice = option.dataset.price || '0';
        const checkbox = item.querySelector('[data-fbt-select]');
        if (checkbox instanceof HTMLInputElement) {
          checkbox.disabled = option.disabled;
          if (option.disabled) checkbox.checked = false;
        }
        item.querySelector('[data-fbt-price]')?.replaceChildren(this.#formatMoney(Number(option.dataset.price || 0)));
      }
    }
    this.#sync();
  };

  #handleCurrentVariant = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;
    const item = this.querySelector('[data-fbt-current-product]');
    if (!item) return;
    item.dataset.fbtPrice = String(variant.price || 0);
    const checkbox = item.querySelector('[data-fbt-select]');
    if (checkbox instanceof HTMLInputElement) {
      checkbox.disabled = !variant.available;
      if (!variant.available) checkbox.checked = false;
    }
    item.querySelector('[data-fbt-price]')?.replaceChildren(this.#formatMoney(Number(variant.price || 0)));
    this.#sync();
  };

  #getSelectedItems() {
    return [...this.querySelectorAll('[data-fbt-item]')].flatMap((item) => {
      const checked = item.querySelector('[data-fbt-select]');
      if (!(checked instanceof HTMLInputElement) || !checked.checked || checked.disabled) return [];
      if (item.hasAttribute('data-fbt-current-product')) {
        const currentId = document.querySelector('product-form-component input[name="id"]')?.value;
        return currentId ? [{ id: currentId, quantity: 1, price: Number(item.dataset.fbtPrice || 0) }] : [];
      }
      const variant = item.querySelector('[data-fbt-variant]');
      if (!(variant instanceof HTMLInputElement || variant instanceof HTMLSelectElement) || !variant.value) return [];
      return [{ id: variant.value, quantity: 1, price: Number(item.dataset.fbtPrice || variant.dataset.price || 0) }];
    });
  }

  #needsVariantChoice() {
    return [...this.querySelectorAll('[data-fbt-item]')].some((item) => {
      const checkbox = item.querySelector('[data-fbt-select]');
      if (!checkbox?.checked || checkbox.disabled) return false;
      const input = item.hasAttribute('data-fbt-current-product')
        ? this.closest('.shopify-section')?.querySelector('product-form-component input[name="id"]')
        : item.querySelector('[data-fbt-variant]');
      return !input?.value;
    });
  }

  #sync() {
    const items = this.#getSelectedItems();
    const total = items.reduce((sum, item) => sum + item.price, 0);
    this.querySelector('[data-fbt-total]')?.replaceChildren(this.#formatMoney(total));
    const button = this.querySelector('[data-fbt-add]');
    if (button instanceof HTMLButtonElement) button.disabled = items.length === 0 || this.#needsVariantChoice();
  }

  #formatMoney(cents) {
    const format = this.dataset.fbtMoneyFormat || '{{amount}}';
    const amount = (cents / 100).toFixed(2);
    const commaAmount = amount.replace('.', ',');
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, commaAmount)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  #setError(message = '') {
    const error = this.querySelector('[data-fbt-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }

  #addSelected = async () => {
    if (this.#needsVariantChoice()) return;
    const items = this.#getSelectedItems();
    if (!items.length) return;
    const button = this.querySelector('[data-fbt-add]');
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = true;
    this.#setError();
    try {
      const formData = new FormData();
      items.forEach(({ id, quantity }, index) => {
        formData.append(`items[${index}][id]`, String(id));
        formData.append(`items[${index}][quantity]`, String(quantity));
      });

      const sections = [...document.querySelectorAll('cart-items-component')]
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch('/cart/add.js', {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || this.dataset.fbtErrorMessage);
      this.dispatchEvent(new CartAddEvent({}, 'mccm-frequently-bought-together', {
        source: 'mccm-frequently-bought-together',
        itemCount: items.length,
        sections: data.sections,
      }));
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        cartDrawer?.querySelector('button[on\\:click="/open"]')?.click();
      }
    } catch (error) {
      console.error(error);
      this.#setError(error instanceof Error ? error.message : this.dataset.fbtErrorMessage);
    } finally {
      this.#sync();
    }
  };
}

if (!customElements.get('mccm-frequently-bought-together')) {
  customElements.define('mccm-frequently-bought-together', MccmFrequentlyBoughtTogether);
}

